import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, X, Check, AlertCircle } from 'lucide-react';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // États pour le modal d'inscription
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
    participants_count: 1,
    participants_details: []
  });
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Fonction pour récupérer les événements depuis l'API
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://localhost:8000/api/public/events');
      
      if (response.ok) {
        const data = await response.json();
        // Adapter les données de l'API au format attendu
        const adaptedEvents = (data.data || data).map(event => ({
          ...event,
          type: event.category,
          icon: getEventIcon(event.category),
          targetAudience: getTargetAudience(event.category),
          recurring: null // À implémenter si nécessaire
        }));
        setEvents(adaptedEvents);
      } else {
        console.error('Erreur lors du chargement des événements');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir l'icône selon la catégorie
  const getEventIcon = (category) => {
    const icons = {
      messe: "💒",
      retraite: "💑",
      adoration: "🙏",
      formation: "📚",
      pelerinage: "✈️",
      celebration: "🎄",
      rencontre: "🍽️"
    };
    return icons[category] || "📅";
  };

  // Fonction pour obtenir le public cible selon la catégorie
  const getTargetAudience = (category) => {
    const audiences = {
      messe: "Tous les couples FSF et leurs enfants",
      retraite: "Couples mariés",
      adoration: "Couples mariés",
      formation: "Couples et futurs mariés",
      pelerinage: "Familles complètes",
      celebration: "Toutes les familles",
      rencontre: "Couples mariés FSF"
    };
    return audiences[category] || "Tous les membres FSF";
  };

  useEffect(() => {
    fetchEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fonction pour afficher une notification
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  // Fonction pour ouvrir le modal d'inscription
  const openRegistrationModal = (event) => {
    setSelectedEvent(event);
    setShowRegistrationModal(true);
    setRegistrationForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
      participants_count: 1,
      participants_details: []
    });
  };

  // Fonction pour fermer le modal d'inscription
  const closeRegistrationModal = () => {
    setShowRegistrationModal(false);
    setSelectedEvent(null);
    setRegistrationForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
      participants_count: 1,
      participants_details: []
    });
  };

  // Fonction pour soumettre l'inscription
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setRegistrationLoading(true);

    try {
      const response = await fetch('http://localhost:8000/api/event-registrations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...registrationForm,
          event_id: selectedEvent.id
        })
      });

      const data = await response.json();

      if (response.ok) {
        showNotification('Inscription enregistrée avec succès ! Vous recevrez une confirmation par email.', 'success');
        closeRegistrationModal();
      } else {
        showNotification(data.message || 'Erreur lors de l\'inscription', 'error');
      }
    } catch (error) {
      showNotification('Erreur de connexion. Veuillez réessayer.', 'error');
    } finally {
      setRegistrationLoading(false);
    }
  };

  // Fonction pour déterminer si un événement nécessite une inscription
  const requiresRegistration = (event) => {
    // Afficher le bouton inscription seulement pour les retraites
    return event.type === 'retraite';
  };

  // Fonction pour obtenir le public cible mis à jour
  const getUpdatedTargetAudience = (event) => {
    if (event.type === 'retraite') {
      return "Ouvert à tout le monde";
    }
    // Pour tous les autres événements
    return "Tous les couples FSF";
  };

  // Données de fallback en cas d'erreur ou pas de données
  const fallbackEvents = [
    {
      id: 1,
      title: "Messe des familles FSF",
      date: "2025-11-03",
      time: "10:00",
      location: "Église Notre-Dame",
      description: "Messe dominicale spécialement célébrée pour les couples et familles de la communauté FSF. Moment de communion fraternelle après la messe.",
      type: "messe",
      recurring: "Chaque dimanche",
      icon: "💒",
      targetAudience: "Tous les couples FSF et leurs enfants"
    },
    {
      id: 2,
      title: "Retraite de couples",
      date: "2025-11-15",
      time: "09:00",
      location: "Centre spirituel Saint-Joseph",
      description: "Week-end de retraite sur le thème 'Grandir ensemble dans l'amour du Christ'. Sessions pour couples avec garde d'enfants assurée.",
      type: "retraite",
      recurring: null,
      icon: "💑",
      targetAudience: "Couples mariés"
    },
    {
      id: 3,
      title: "Soirée prière en couple",
      date: "2025-11-01",
      time: "19:00",
      location: "Chapelle du Saint-Sacrement",
      description: "Temps d'adoration eucharistique et de prière spécialement organisé pour les couples. Prière pour les intentions matrimoniales.",
      type: "adoration",
      recurring: "Premier vendredi du mois",
      icon: "🙏",
      targetAudience: "Couples mariés"
    },
    {
      id: 4,
      title: "Formation 'Théologie du mariage'",
      date: "2025-11-07",
      time: "20:00",
      location: "Salle paroissiale",
      description: "Cycle de formation sur la spiritualité conjugale et familiale. Cette semaine : 'La Sainte Famille, modèle pour nos foyers'.",
      type: "formation",
      recurring: "Chaque jeudi",
      icon: "📚",
      targetAudience: "Couples et futurs mariés"
    },
    {
      id: 5,
      title: "Pèlerinage familial à Lourdes",
      date: "2025-12-08",
      time: "06:00",
      location: "Départ de la paroisse",
      description: "Pèlerinage de 3 jours à Lourdes spécialement organisé pour les familles. Prières pour les couples et bénédiction des alliances.",
      type: "pelerinage",
      recurring: null,
      icon: "✈️",
      targetAudience: "Familles complètes"
    },
    {
      id: 6,
      title: "Veillée de Noël des familles",
      date: "2025-12-24",
      time: "23:00",
      location: "Église Notre-Dame",
      description: "Veillée et messe de la Nativité avec participation spéciale des familles FSF. Crèche vivante animée par nos enfants.",
      type: "celebration",
      recurring: null,
      icon: "🎄",
      targetAudience: "Toutes les familles"
    },
    {
      id: 7,
      title: "Soirée de rencontre des couples",
      date: "2025-11-10",
      time: "19:30",
      location: "Salle Saint-Joseph",
      description: "Soirée conviviale entre couples FSF : partage d'expériences, témoignages et moment fraternels autour d'un repas partagé.",
      type: "rencontre",
      recurring: "Deuxième samedi du mois",
      icon: "🍽️",
      targetAudience: "Couples mariés FSF"
    },
    {
      id: 8,
      title: "Préparation au mariage",
      date: "2025-11-20",
      time: "14:00",
      location: "Centre paroissial",
      description: "Session de préparation au mariage chrétien animée par des couples expérimentés de la communauté FSF.",
      type: "formation",
      recurring: "Mensuel",
      icon: "💍",
      targetAudience: "Fiancés"
    }
  ];

  const getEventTypeColor = (type) => {
    const colors = {
      messe: "bg-fsf-primary/10 text-fsf-primary border-fsf-primary",
      retraite: "bg-fsf-marriage/10 text-fsf-marriage border-fsf-marriage",
      adoration: "bg-fsf-secondary/10 text-fsf-secondary border-fsf-secondary",
      formation: "bg-fsf-accent/10 text-fsf-accent border-fsf-accent",
      pelerinage: "bg-fsf-family/10 text-fsf-family border-fsf-family",
      celebration: "bg-fsf-gold/10 text-fsf-gold border-fsf-gold",
      rencontre: "bg-pink-100 text-pink-700 border-pink-300"
    };
    return colors[type] || "bg-gray-100 text-gray-700 border-gray-300";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isUpcoming = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    return eventDate >= today;
  };

  // Utiliser les événements de l'API ou les événements de fallback si pas de données
  const eventsToShow = events.length > 0 ? events : fallbackEvents;
  const upcomingEvents = eventsToShow.filter(event => isUpcoming(event.date));

  return (
    <section id="evenements" className="py-20 bg-gradient-to-br from-fsf-warm/10 via-white to-fsf-light/20">
      <div className="container mx-auto px-4">
        {/* <div className="text-center mb-16">
          <div className="animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-fsf-primary mb-6 font-serif">
              Événements de la communauté
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto mb-8 leading-relaxed text-justify">
              Rejoignez-nous lors de nos <span className="font-bold text-fsf-marriage">célébrations de couples</span>, 
              formations matrimoniales et moments de partage en famille chrétienne.
            </p>
            <div className="flex items-center justify-center space-x-4 text-fsf-accent">
              <span className="text-2xl">💒</span>
              <span className="font-medium text-lg">Grandir ensemble dans la foi</span>
              <span className="text-2xl">👨‍👩‍👧‍👦</span>
            </div>
          </div>
        </div> */}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des événements...</p>
          </div>
        )}

        {/* Events grid */}
        {!loading && (
          <div className="grid lg:grid-cols-2 gap-10 max-w-7xl mx-auto">
            {upcomingEvents.map((event, index) => (
            <div key={event.id} className="bg-gradient-to-br from-white to-fsf-light/20 rounded-2xl shadow-2xl p-8 hover:shadow-3xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 animate-fade-in-up" style={{animationDelay: `${index * 0.1}s`}}>
              
              {/* Header de l'événement */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-fsf-primary to-fsf-secondary rounded-xl flex items-center justify-center text-3xl shadow-lg">
                    {event.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-bold border-2 ${getEventTypeColor(event.type)}`}>
                        {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                      </span>
                      {event.recurring && (
                        <span className="text-sm text-fsf-accent bg-fsf-warm/20 px-3 py-1 rounded-full font-medium">
                          🔄 {event.recurring}
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-fsf-primary mb-2 font-serif">
                      {event.title}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Public cible */}
              <div className="bg-fsf-warm/20 rounded-lg p-3 mb-4 border-l-4 border-fsf-accent">
                <div className="flex items-center space-x-2">
                  <span className="text-fsf-accent font-bold text-sm">🎯 Public :</span>
                  <span className="text-fsf-primary font-medium text-sm">{getUpdatedTargetAudience(event)}</span>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-3 text-gray-600">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">{formatDate(event.date)}</span>
                </div>

                <div className="flex items-center space-x-3 text-gray-600">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>{event.time}</span>
                </div>

                <div className="flex items-center space-x-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <span>{event.location}</span>
                </div>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed text-justify">
                {event.description}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-gray-500">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">{getUpdatedTargetAudience(event)}</span>
                </div>
                {requiresRegistration(event) && (
                  <button 
                    onClick={() => openRegistrationModal(event)}
                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-amber-600 text-white rounded-lg hover:from-blue-700 hover:to-amber-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    S'inscrire
                  </button>
                )}
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Modal d'inscription */}
        {showRegistrationModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                {/* Header du modal */}
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Inscription à l'événement
                    </h2>
                    <p className="text-lg font-semibold text-blue-600">{selectedEvent.title}</p>
                    <p className="text-gray-600">
                      {formatDate(selectedEvent.date)} à {selectedEvent.time}
                    </p>
                  </div>
                  <button
                    onClick={closeRegistrationModal}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                {/* Formulaire d'inscription */}
                <form onSubmit={handleRegistrationSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Prénom */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        required
                        value={registrationForm.first_name}
                        onChange={(e) => setRegistrationForm({
                          ...registrationForm,
                          first_name: e.target.value
                        })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="Votre prénom"
                      />
                    </div>

                    {/* Nom */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nom *
                      </label>
                      <input
                        type="text"
                        required
                        value={registrationForm.last_name}
                        onChange={(e) => setRegistrationForm({
                          ...registrationForm,
                          last_name: e.target.value
                        })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={registrationForm.email}
                        onChange={(e) => setRegistrationForm({
                          ...registrationForm,
                          email: e.target.value
                        })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="votre.email@example.com"
                      />
                    </div>

                    {/* Téléphone */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        required
                        value={registrationForm.phone}
                        onChange={(e) => setRegistrationForm({
                          ...registrationForm,
                          phone: e.target.value
                        })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                        placeholder="+229 XX XX XX XX"
                      />
                    </div>
                  </div>

                  {/* Nombre de participants */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nombre de participants *
                    </label>
                    <select
                      value={registrationForm.participants_count}
                      onChange={(e) => setRegistrationForm({
                        ...registrationForm,
                        participants_count: parseInt(e.target.value)
                      })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? 'personne' : 'personnes'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message optionnel */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message ou questions (optionnel)
                    </label>
                    <textarea
                      value={registrationForm.message}
                      onChange={(e) => setRegistrationForm({
                        ...registrationForm,
                        message: e.target.value
                      })}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="Informations supplémentaires, besoins spéciaux, questions..."
                    />
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex space-x-4 pt-6">
                    <button
                      type="button"
                      onClick={closeRegistrationModal}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={registrationLoading}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-amber-600 text-white rounded-lg hover:from-blue-700 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {registrationLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Inscription en cours...
                        </div>
                      ) : (
                        'Confirmer l\'inscription'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span>{notification.message}</span>
          </div>
        )}

        {/* Calendrier mensuel simplifié */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-amber-50 rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Calendrier mensuel des événements
          </h3>
          
          <div className="grid grid-cols-7 gap-2 text-center">
            {/* En-têtes des jours */}
            {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
              <div key={day} className="font-bold text-gray-600 py-2">
                {day}
              </div>
            ))}
            
            {/* Jours du mois (exemple simplifié) */}
            {Array.from({ length: 30 }, (_, i) => i + 1).map(day => {
              const hasEvent = events.some(event => {
                const eventDay = new Date(event.date).getDate();
                return eventDay === day;
              });
              
              return (
                <div
                  key={day}
                  className={`p-2 rounded ${
                    hasEvent 
                      ? 'bg-blue-600 text-white font-bold' 
                      : 'bg-white hover:bg-blue-100'
                  } transition-colors duration-200 cursor-pointer`}
                >
                  {day}
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>Les jours en bleu indiquent des événements programmés</p>
          </div>
        </div>

        {/* Section d'inscription */}
        {/* <div className="mt-16 text-center">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Restez informé de nos événements
            </h3>
            <p className="text-gray-600 mb-6 text-justify">
              Inscrivez-vous à notre newsletter pour recevoir les dernières informations 
              sur nos événements et activités communautaires.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200">
                S'inscrire
              </button>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default Events;